/**
 * RP92 (D11, ADM-A1) — Carte « Terminer la configuration » du tableau de bord.
 *
 * Affichée à l'administrateur tant que l'assistant « Premier jour » n'est pas terminé
 * (GET /api/admin/premier-jour → a_proposer). Liste de vérification (x/5) et un seul
 * bouton qui ouvre l'assistant (/premier-jour, qui met les envois d'e-mails en pause).
 * Rien n'est affiché aux autres rôles ni si l'appel échoue.
 *
 * B6 (P1#27) — à la première connexion de l'administrateur d'un cabinet sans chantier réel
 * (`ouvrir_automatiquement`), l'assistant s'ouvre tout seul, une seule fois ; ensuite
 * seule cette carte le propose. 5ᵉ élément : « Votre premier chantier ».
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Circle, ArrowRight, X } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ELEMENTS = [
  { id: 'cabinet', libelle: 'Identité du cabinet (logo, mentions)' },
  { id: 'equipe', libelle: "Inviter l'équipe" },
  { id: 'entreprises', libelle: 'Premières entreprises' },
  { id: 'envois', libelle: "Reprendre les envois d'e-mails" },
  { id: 'premier_chantier', libelle: 'Votre premier chantier (créer, importer ou projet exemple)' },
];

export default function CartePremierJour() {
  const { isAdmin } = useAuth();
  const [etat, setEtat] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/admin/premier-jour`, { withCredentials: true })
      .then(async (r) => {
        setEtat(r.data);
        // B6 — première connexion d'un cabinet sans chantier réel : l'assistant s'ouvre seul (une fois)
        if (r.data?.ouvrir_automatiquement) {
          try {
            await axios.post(`${API_URL}/api/admin/premier-jour/ouvert-automatiquement`, null, { withCredentials: true });
          } catch { /* l'assistant s'ouvre quand même */ }
          navigate('/premier-jour');
        }
      })
      .catch(() => setEtat(null));
  }, [isAdmin, navigate]);

  if (!isAdmin || !etat?.a_proposer) return null;

  const masquer = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/premier-jour/ignorer`, null, { withCredentials: true });
      setEtat(data);
    } catch { /* la carte reste affichée */ }
  };

  return (
    <div className="border border-[#C75B39]/40 bg-[#FFF8F4] p-5 mb-8" data-testid="carte-premier-jour">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[#2C2C2C]">
            Terminer la configuration ({etat.faites}/{etat.total})
          </h2>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Quelques minutes pour mettre le cabinet en route.
            {etat.pause_envois && " Les envois d'e-mails sont en pause jusqu'à la fin."}
          </p>
        </div>
        <button type="button" onClick={masquer} className="text-[#9A9A9A] hover:text-[#2C2C2C]" title="Ne plus proposer" aria-label="Ne plus proposer">
          <X size={16} />
        </button>
      </div>
      <ul className="mt-3 grid sm:grid-cols-2 gap-1 text-sm">
        {ELEMENTS.map((e) => (
          <li key={e.id} className="flex items-center gap-2">
            {etat.etapes?.[e.id]
              ? <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
              : <Circle size={16} className="text-[#9A9A9A]" />}
            <span className={etat.etapes?.[e.id] ? 'text-[#6B6B6B] line-through' : 'text-[#2C2C2C]'}>{e.libelle}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/premier-jour"
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#2C2C2C] text-white text-sm hover:bg-[#4A4A4A]"
        data-testid="carte-premier-jour-ouvrir"
      >
        Terminer la configuration <ArrowRight size={16} />
      </Link>
    </div>
  );
}

/** Paramètres › Premier jour : rouvrir l'assistant à tout moment (administrateur). */
export function EntreePremierJour() {
  return (
    <div className="border border-[#E0E0DB] bg-white p-5 max-w-2xl" data-testid="settings-premier-jour">
      <h2 className="text-lg font-semibold text-[#2C2C2C]">Assistant « Premier jour »</h2>
      <p className="text-sm text-[#6B6B6B] mt-1">
        Identité du cabinet, invitation de l'équipe, premières entreprises, reprise des envois d'e-mails, puis votre premier chantier (créer, importer ou projet exemple).
      </p>
      <Link to="/premier-jour" className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#2C2C2C] text-white text-sm hover:bg-[#4A4A4A]">
        Ouvrir l'assistant <ArrowRight size={16} />
      </Link>
    </div>
  );
}
